import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DndContext, DragEndEvent, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, RefreshCw, Loader2 } from "lucide-react";

export interface GridImage {
  id: string;
  url: string;
  caption: string | null;
}

export type GridMove = "up" | "down" | "top" | "bottom";

interface Props {
  images: GridImage[];
  layout: string; // single | grid-2 | grid-3 | mixed
  onReorder: (activeId: string, overId: string) => void;
  onDelete: (id: string) => void;
  onReplace: (id: string, file: File) => Promise<void>;
  onCaptionSave: (id: string, caption: string) => void;
  large?: boolean;
}

// Samma kolumn-känsla som jobbet har på hemsidan.
const columnsClass = (_layout: string, large?: boolean) =>
  large
    ? "grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9"
    : "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7";

const Tile = ({
  img,
  index,
  onDelete,
  onReplace,
  onCaptionSave,
}: {
  img: GridImage;
  index: number;
  onDelete: Props["onDelete"];
  onReplace: Props["onReplace"];
  onCaptionSave: Props["onCaptionSave"];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });
  const fileRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);
  const [caption, setCaption] = useState(img.caption || "");

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setReplacing(true);
    try {
      await onReplace(img.id, file);
    } finally {
      setReplacing(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`bg-card border rounded-md overflow-hidden flex flex-col ${isDragging ? "shadow-xl ring-2 ring-primary relative z-20" : ""}`}
    >
      <div className="relative bg-muted">
        <img src={img.url} alt={img.caption || ""} className="w-full aspect-square object-cover block" loading="lazy" />
        <div className="absolute top-2 left-2 bg-background/90 text-xs font-medium rounded px-2 py-0.5">{index + 1}</div>
        {replacing && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        <div
          {...attributes}
          {...listeners}
          title="Dra för att flytta"
          className="absolute top-2 right-2 bg-background/90 hover:bg-background rounded p-1.5 cursor-grab active:cursor-grabbing touch-none shadow"
        >
          <GripVertical className="w-5 h-5" />
        </div>
      </div>

      <div className="p-1.5 space-y-1.5">
        <Input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={() => caption !== (img.caption || "") && onCaptionSave(img.id, caption)}
          onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
          placeholder="Namn"
          className="h-7 text-xs px-2"
        />
        <div className="flex items-center gap-1 flex-wrap">
          <div className="flex-1" />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            title="Byt ut bilden men behåll namn och plats"
            disabled={replacing}
            onClick={() => fileRef.current?.click()}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Byt
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" title="Ta bort" onClick={() => onDelete(img.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImageGrid = ({ images, layout, onReorder, onDelete, onReplace, onCaptionSave, large }: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  if (images.length === 0) {
    return <p className="text-muted-foreground">Inga bilder ännu.</p>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div className={`grid gap-4 items-start ${columnsClass(layout, large)}`}>
          {images.map((img, idx) => (
            <Tile
              key={img.id}
              img={img}
              index={idx}
              onDelete={onDelete}
              onReplace={onReplace}
              onCaptionSave={onCaptionSave}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ImageGrid;
